import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "../utils/base_url";
import "../styles/stocknotify.css";
import { useAuth } from "../context/AuthContext";

const StockNotifyModal = ({
  open,
  onClose,
  product,
  notifyForm,
  setNotifyForm,
  navigate,
}) => {
  const { user } = useAuth();
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);

  useEffect(() => {
    if (open && user?.email) {
      setNotifyForm((prev) => ({ ...prev, email: user.email }));
    }
    if (!open) {
      setEmailError("");
      setEmailTouched(false);
    }
  }, [open, user, setNotifyForm]);

  if (!open) return null;

  const validateEmail = (value) => {
    if (!value) return "Email address is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      return "Please enter a valid email address";
    return "";
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setNotifyForm((prev) => ({ ...prev, email: val }));
    if (emailTouched) setEmailError(validateEmail(val));
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(notifyForm.email));
  };

  const changeQty = (delta) => {
    const current = Number(notifyForm.quantity) || 1;
    const next = Math.min(200, Math.max(1, current + delta));
    setNotifyForm((prev) => ({ ...prev, quantity: next }));
  };

  const handleQtyInput = (e) => {
    const val = Math.min(200, Math.max(1, Number(e.target.value) || 1));
    setNotifyForm((prev) => ({ ...prev, quantity: val }));
  };

  const submitStockNotify = async () => {
    if (!user) {
      toast.error("Please login first!");
      navigate("/login");
      return;
    }

    setEmailTouched(true);
    const err = validateEmail(notifyForm.email);
    setEmailError(err);
    if (err) return;

    const finalQty = Number(notifyForm.quantity) || 1;

    if (finalQty < 1) {
      toast.error("Please enter a valid quantity");
      return;
    }

    try {
      await api.post("stocknotify/", {
        user_id: user?.customer_details?.id,
        product_id: product?.id,
        email: notifyForm.email,
        quantity: finalQty,
      });

      toast.success("Notification request submitted");

      setNotifyForm({ email: user?.email || "", quantity: 1 });
      setEmailTouched(false);
      setEmailError("");
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit");
    }
  };

  const qty = Number(notifyForm.quantity) || 1;

  return (
    <div className="notify-modal-overlay" onClick={onClose}>
      <div className="notify-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="notify-header">
          <div className="notify-icon-wrap">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </div>
          <h4 className="notify-title">Stock Notification</h4>
          <button className="notify-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Product chip */}
        <div className="notify-product-chip">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          </svg>
          <span className="notify-product-label">Product:</span>
          <strong>{product?.product_name}</strong>
        </div>

        {/* Email — floating label */}
        <div className={`notify-field-wrap ${emailError ? "has-error" : ""}`}>
          <input
            id="notify-email"
            type="email"
            className={`notify-float-input ${notifyForm.email ? "filled" : ""}`}
            value={notifyForm.email || ""}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            placeholder=" "
            autoComplete="email"
          />
          <label htmlFor="notify-email" className="notify-float-label">
            Email address <span className="notify-required">*</span>
          </label>
          {emailError && (
            <p className="notify-field-error">{emailError}</p>
          )}
        </div>

        <div className="notify-divider" />

        {/* Quantity stepper */}
        <div className="notify-stepper-wrap">
          <p className="notify-stepper-label">
            Required quantity <span className="notify-required">*</span>
          </p>
          <div className="notify-stepper">
            <button
              className="notify-stepper-btn"
              onClick={() => changeQty(-1)}
              disabled={qty <= 1}
              aria-label="Decrease quantity"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>

            <input
              type="number"
              className="notify-stepper-input"
              value={qty}
              min={1}
              max={200}
              onChange={handleQtyInput}
              aria-label="Quantity"
            />

            <button
              className="notify-stepper-btn"
              onClick={() => changeQty(1)}
              disabled={qty >= 200}
              aria-label="Increase quantity"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>
          <div className="notify-range-row">
            <span>Min: 1</span>
            <span>Max: 200</span>
          </div>
        </div>

        {/* Actions */}
        <div className="notify-actions">
          <button className="notify-btn notify-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="notify-btn notify-btn-submit" onClick={submitStockNotify}>
            Notify me
          </button>
        </div>

      </div>
    </div>
  );
};

export default StockNotifyModal;